import React, { useState, useEffect, useRef } from "react";
import { 
  Box, Input, InputGroup, InputRightElement, Button, Text, Link, 
  HStack, Spinner, Divider 
} from "@chakra-ui/react";
import { useHistory } from "react-router-dom";
import EyeOpen from "assets/svg/eye-open.svg";
import EyeClose from "assets/svg/eye-close.svg";
import AppleLogo from "assets/svg/apple.svg";
import GoogleLogo from "assets/svg/google.svg";
import SignUpSteps from "../signup/SignUpSteps";
import EmailVerification from "../signup/EmailVerification";
import ForgotPassword from "../forgot_password/ForgotPassword";
import { useAuth } from "auth/authProvider";
import { handleGoogleSignIn, handleAppleSignIn } from "auth/firebase/firebase_config";

const FIELD_W = "260px";
const inputSt = { 
  bg: "transparent", border: "1px solid #fff", borderRadius: "10px", 
  color: "#fff", fontSize: "13px", p: "10px", w: "100%" 
};

export default function LoginForm() {
  const { login, socialLogin } = useAuth();
  const history = useHistory();
  const isMountedRef = useRef(true);
  
  const [state, setState] = useState({
    show: false, username: "", password: "", isSignup: false,
    showVerification: false, verificationEmail: "", error: "", isLoading: false,
    showForgotPassword: false
  });

  const updateState = (updates) => setState(prev => ({ ...prev, ...updates }));

  useEffect(() => () => { isMountedRef.current = false; }, []);

  useEffect(() => {
    const u = localStorage.getItem("username"), p = localStorage.getItem("password");
    if (u && p) updateState({ username: u, password: p });
  }, []);

  if (state.isSignup) return <SignUpSteps onBack={() => updateState({ isSignup: false })} />;
  
  if (state.showVerification) {
    return (
      <EmailVerification 
        email={state.verificationEmail}
        onBack={() => updateState({ showVerification: false, verificationEmail: "" })}
        onVerified={() => {}}
      />
    );
  }

  if (state.showForgotPassword) {
    return (
      <ForgotPassword 
        onBack={() => updateState({ showForgotPassword: false })}
      />
    );
  }

  const handleSubmit = async e => {
    e.preventDefault();
    updateState({ error: "" });
    
    if (!state.username || !state.password) {
      if (isMountedRef.current) updateState({ error: "Username and password are required." });
      return;
    }
  
    if (isMountedRef.current) updateState({ isLoading: true });
    
    try {
      const result = await login(state.username, state.password);
      
      if (result?.needsVerification) {
        if (isMountedRef.current) {
          updateState({ verificationEmail: result.email || "", showVerification: true, isLoading: false });
        }
        return;
      }
    } catch (err) {
      let errorMessage = "Invalid password. Please try again.";
      if (err.message === "USER_NOT_EXISTS") {
        errorMessage = "No GRID account found with these credentials. Please sign up first.";
      }
      if (isMountedRef.current) updateState({ error: errorMessage });
    } finally {
      if (isMountedRef.current) updateState({ isLoading: false });
    }
  };

  const handleSocialLogin = async (provider, isGoogle) => {
    if (isMountedRef.current) updateState({ error: "", isLoading: true });
    try {
      const user = await provider();
      await socialLogin(user.email, user.uid, isGoogle);
    } catch (error) {
      if (isMountedRef.current) {
        let errorMessage;
        if (error.code === "USER_NOT_EXISTS") {
          errorMessage = `No GRID account found with this ${isGoogle ? 'Google' : 'Apple'} email. Please sign up first.`;
        } else if (error.message === "Canceled") {
          errorMessage = "Account linking was canceled.";
        } else {
          errorMessage = `${isGoogle ? 'Google' : 'Apple'} login failed. Please try again.`;
        }
        updateState({ error: errorMessage });
      }
    } finally {
      if (isMountedRef.current) updateState({ isLoading: false });
    }
  };

  const SocialButton = ({ onClick, logo, label }) => (
    <Button onClick={onClick} display="flex" alignItems="center" gap="8px" 
      w="125px" h="40px" bg="transparent" color="#fff" border="1px solid #fff" 
      borderRadius="10px" fontSize="13px" _hover={{ bg: "#fff", color: "#000" }}>
      <img src={logo} alt={label} width={label === "Google" ? 16 : 18} />
      {label}
    </Button>
  );

  return (
    <Box as="form" onSubmit={handleSubmit} display="flex" flexDirection="column" alignItems="center" px="25px">
      <Text color="#fff" fontSize="26px" mb="6px">Login</Text>
      <Text color="#ADA8C3" fontSize="12px" mb="28px">Generative AI made simple.</Text>

      <HStack spacing="12px" mb="20px">
        <SocialButton onClick={() => handleSocialLogin(handleGoogleSignIn, true)} logo={GoogleLogo} label="Google" />
        <SocialButton onClick={() => handleSocialLogin(handleAppleSignIn, false)} logo={AppleLogo} label="Apple" />
      </HStack>

      <HStack w={FIELD_W} mb="18px">
        <Divider borderColor="#ADA8C3" />
        <Text color="#ADA8C3" fontSize="12px" px="10px">or</Text>
        <Divider borderColor="#ADA8C3" />
      </HStack>

      <InputGroup w={FIELD_W} mb="18px">
        <Input {...inputSt} type="text" placeholder="Username *" required 
          value={state.username} onChange={e => updateState({ username: e.target.value })} />
      </InputGroup>

      <InputGroup w={FIELD_W} mb="4px">
        <Input {...inputSt} type={state.show ? "text" : "password"} placeholder="Password *" 
          required pr="3rem" value={state.password} onChange={e => updateState({ password: e.target.value })} />
        <InputRightElement width="3rem" height="100%">
          <Box h="1.5rem" display="flex" alignItems="center" justifyContent="center" 
            bg="transparent" onClick={() => updateState({ show: !state.show })} cursor="pointer">
            <img src={state.show ? EyeClose : EyeOpen} alt="toggle" width={20} />
          </Box>
        </InputRightElement>
      </InputGroup>

      <Box w={FIELD_W} display="flex" justifyContent="flex-end" mb="4px">
        <Text 
          fontSize="12px" 
          color="#ADA8C3" 
          cursor="pointer"
          _hover={{ color: "#fff" }}
          onClick={() => updateState({ showForgotPassword: true })}
        >
          Forgot Password?
        </Text>
      </Box>

      {state.error && (
        <Text color="red" fontSize="12px" mt="8px" textAlign="center" fontWeight="bold">
          {state.error}
        </Text>
      )}

      {state.isLoading ? (
        <Spinner color="white" mt="15px" w="30px" h="30px" thickness="4px" speed="0.65s" />
      ) : (
        <Button type="submit" w={FIELD_W} mt="20px" p="10px" bg="#fff" color="#000" 
          borderRadius="10px" disabled={!state.username || !state.password || state.isLoading}
          _hover={{ cursor: "pointer", bg: "transparent", color: "#fff", outline: "1px solid #fff" }}>
          Login
        </Button>
      )}

      <Text fontSize="12px" color="#fff" mt="15px" textAlign="center">
        Don't have an account?{' '}
        <Link href="#" fontWeight="bold" color="#fff" 
          onClick={e => { e.preventDefault(); updateState({ isSignup: true }); }}>
          Sign up
        </Link>
      </Text>
    </Box>
  );
}